import React, { useState } from 'react';
import { X, Share2, Check, ChevronDown, Clock, BookOpen } from 'lucide-react';

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-emerald-100 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left px-4 py-3 hover:bg-emerald-50">
        <span className="text-sm font-medium" style={{ color: '#0d3d29' }}>{q}</span>
        <ChevronDown className={`w-4 h-4 text-emerald-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-emerald-900/90 leading-relaxed border-t border-emerald-50">{a}</div>
      )}
    </div>
  );
};

const BlogArticleModal = ({ article, onClose }) => {
  const [copied, setCopied] = useState(false);

  const share = () => {
    const url = `${window.location.origin}/?guide=${article.id}`;
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      window.prompt('Copy this link:', url);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[95vh] overflow-y-auto rounded-3xl shadow-2xl" style={{ backgroundColor: '#F4F9F5' }} onClick={(e) => e.stopPropagation()}>
        <div className="relative aspect-[21/9] bg-black">
          <img src={article.image} alt={article.title} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button onClick={share} className="inline-flex items-center gap-1.5 rounded-full bg-white/90 hover:bg-white px-3 py-1.5 text-xs font-medium" style={{ color: '#0d3d29' }}>
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share Guide</>}
            </button>
            <button onClick={onClose} className="p-2 rounded-full bg-white/90 hover:bg-white" aria-label="Close">
              <X className="w-5 h-5" style={{ color: '#0d3d29' }} />
            </button>
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5">{article.categoryEmoji} {article.category}</span>
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readMinutes}-min read</span>
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5">{article.date}</span>
            </div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl leading-tight max-w-3xl">{article.title}</h2>
          </div>
        </div>

        <div className="p-5 sm:p-8">
          <div className="prose-emerald max-w-none space-y-4">
            {article.body && article.body.length > 0 ? (
              article.body.map((block, i) => {
                if (block.type === 'h') return <h3 key={i} className="font-serif text-xl mt-6" style={{ color: '#1E563E' }}>{block.text}</h3>;
                return <p key={i} className="text-[15px] text-emerald-900/90 leading-relaxed">{block.text}</p>;
              })
            ) : article.content ? (
              article.content.split(/\n\s*\n/).map((chunk, i) => {
                const t = chunk.trim();
                if (t.startsWith('## ')) return <h3 key={i} className="font-serif text-xl mt-6" style={{ color: '#1E563E' }}>{t.slice(3).trim()}</h3>;
                if (t.startsWith('# ')) return <h2 key={i} className="font-serif text-2xl mt-6" style={{ color: '#0d3d29' }}>{t.slice(2).trim()}</h2>;
                return <p key={i} className="text-[15px] text-emerald-900/90 leading-relaxed whitespace-pre-wrap">{t}</p>;
              })
            ) : (
              <p className="text-emerald-800/70">No content yet.</p>
            )}
          </div>

          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-widest mb-2 inline-flex items-center gap-1" style={{ color: '#1E563E' }}><BookOpen className="w-3 h-3" /> Companion Session</div>
            <div className="rounded-2xl overflow-hidden border border-emerald-100 bg-black aspect-video">
              <iframe
                title={`Companion session for ${article.title}`}
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${article.youtubeId}?playsinline=1&controls=1&rel=0&modestbranding=1&iv_load_policy=3`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
              />
            </div>
            <div className="mt-2 text-xs text-emerald-700/80">
              If the video shows "Video unavailable", <a className="underline" style={{ color: '#1E563E' }} href={`https://www.youtube.com/watch?v=${article.youtubeId}`} target="_blank" rel="noreferrer">open it on YouTube</a>.
            </div>
          </div>

          {article.faqs && article.faqs.length > 0 && (
            <div className="mt-8">
              <h4 className="font-serif text-2xl mb-3" style={{ color: '#0d3d29' }}>Frequently Asked</h4>
              <div className="space-y-2">
                {article.faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
              </div>
            </div>
          )}

          <div className="mt-10 rounded-2xl p-5 flex flex-wrap items-center gap-3 justify-between" style={{ backgroundColor: '#e6f0eb' }}>
            <div>
              <div className="font-serif text-lg" style={{ color: '#0d3d29' }}>Enjoyed this guide?</div>
              <div className="text-xs text-emerald-800/80">Share it with someone who could use a moment of calm today.</div>
            </div>
            <button onClick={share} className="inline-flex items-center gap-2 rounded-full text-white text-sm px-4 py-2 hover:opacity-90" style={{ backgroundColor: '#1E563E' }}>
              {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Share2 className="w-4 h-4" /> Share Guide</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogArticleModal;
